import React, { useState, useEffect, useCallback } from 'react';
import {
  BellIcon, PaperAirplaneIcon, UsersIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import styles from './NotificationsPage.module.css';

const AUDIENCES = [
  { value: 'all',              label: 'All Users',              desc: 'Everyone with the app installed' },
  { value: 'vip_active',       label: 'Active VIP Members',     desc: 'Currently active VIP members' },
  { value: 'registered_no_vip',label: 'Registered — No VIP',    desc: 'Users who never purchased VIP' },
  { value: 'expiring_30',      label: 'Expiring in 30 Days',    desc: 'VIP memberships expiring soon' },
  { value: 'expiring_60',      label: 'Expiring in 31–60 Days', desc: 'Proactive renewal targets' },
  { value: 'churned',          label: 'Churned — Expired VIP',  desc: 'Win-back campaign audience' },
];

const TEMPLATES = [
  { label: 'Eid Greeting',        title: 'Eid Mubarak from The Toy Store!', body: 'Wishing you and your family a joyful Eid. Visit us in-store for exclusive VIP offers.' },
  { label: 'Renewal Reminder',    title: 'Your VIP is expiring soon!',       body: 'Renew your VIP membership now and keep enjoying 25% off all toys.' },
  { label: 'New Arrivals',        title: 'New toys just arrived!',           body: 'Exclusive new arrivals in-store. Visit us and enjoy your VIP discount.' },
  { label: 'Birthday Surprise',   title: 'Happy Birthday!',                  body: 'The Toy Store wishes you a magical birthday. A special surprise is waiting for you!' },
  { label: 'Campaign Offer',      title: 'Exclusive VIP Offer!',             body: 'Special discount available for VIP members this week only. Visit the store today.' },
];

export default function NotificationsPage() {
  const [campaigns, setCampaigns]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState({ title: '', body: '', audience: 'all' });
  const [sending, setSending]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getPushCampaigns();
      setCampaigns(r.campaigns || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleTemplate = (t) => setForm(f => ({ ...f, title: t.title, body: t.body }));

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return setError('Title and message are required.');
    if (!window.confirm(`Send "${form.title}" to ${AUDIENCES.find(a => a.value === form.audience)?.label}?`)) return;
    setSending(true); setError(''); setResult(null);
    try {
      const r = await adminAPI.sendPushNotification(form);
      setResult(r);
      setForm({ title: '', body: '', audience: 'all' });
      loadCampaigns();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Push Notifications</h1>
          <p className={styles.pageSub}>Send targeted messages to users by segment or campaign.</p>
        </div>
      </div>

      <div className={styles.layout}>
        {/* ── Compose ── */}
        <div className={styles.composeCard}>
          <div className={styles.cardHeader}>
            <BellIcon style={{ width: 18, height: 18 }} />
            <span>Compose Notification</span>
          </div>

          {/* Templates */}
          <div className={styles.templates}>
            <div className={styles.templatesLabel}>Quick Templates</div>
            <div className={styles.templateGrid}>
              {TEMPLATES.map(t => (
                <button key={t.label} className={styles.templateBtn} onClick={() => handleTemplate(t)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSend} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Audience</label>
              <select className={styles.input} value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}>
                {AUDIENCES.map(a => (
                  <option key={a.value} value={a.value}>{a.label} — {a.desc}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Title *</label>
              <input
                className={styles.input}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Notification title..."
                maxLength={65}
              />
              <span className={styles.charCount}>{form.title.length}/65</span>
            </div>

            <div className={styles.formGroup}>
              <label>Message *</label>
              <textarea
                className={styles.textarea}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your message here..."
                rows={4}
                maxLength={240}
              />
              <span className={styles.charCount}>{form.body.length}/240</span>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            {result && (
              <div className={styles.successBanner}>
                <CheckCircleIcon style={{ width: 16, height: 16 }} />
                Sent to {result.sentCount} device{result.sentCount !== 1 ? 's' : ''}
                {result.failCount > 0 && ` (${result.failCount} failed)`}
              </div>
            )}

            <button type="submit" className={styles.btnSend} disabled={sending}>
              <PaperAirplaneIcon style={{ width: 16, height: 16 }} />
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>

        {/* ── History ── */}
        <div className={styles.historyCard}>
          <div className={styles.cardHeader}>
            <UsersIcon style={{ width: 18, height: 18 }} />
            <span>Sent Campaigns</span>
          </div>

          {loading ? (
            <div className={styles.skeleton}>
              {[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
            </div>
          ) : campaigns.length === 0 ? (
            <div className={styles.empty}>No campaigns sent yet.</div>
          ) : (
            <div className={styles.campaignList}>
              {campaigns.map(c => (
                <div key={c.id} className={styles.campaignItem}>
                  <div className={styles.campaignTop}>
                    <span className={styles.campaignTitle}>{c.title}</span>
                    <span className={styles.audienceBadge}>{AUDIENCES.find(a => a.value === c.audience)?.label || c.audience}</span>
                  </div>
                  <p className={styles.campaignBody}>{c.body}</p>
                  <div className={styles.campaignMeta}>
                    <span className={styles.campaignSent}>{c.sent_count} sent</span>
                    {/* Read comes from the member opening it in the app. Sent only
                        means Expo accepted the push, which is not the same thing. */}
                    {c.recipients > 0 && (
                      <span className={styles.campaignRead}>
                        {c.read_count} read
                        <span className={styles.campaignReadPct}>
                          {" "}({Math.round((c.read_count / c.recipients) * 100)}%)
                        </span>
                      </span>
                    )}
                    {c.fail_count > 0 && <span className={styles.campaignFail}>{c.fail_count} failed</span>}
                    <span className={styles.campaignDate}>{new Date(c.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span className={styles.campaignBy}>by {c.sent_by_name || 'Admin'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
